import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Feature, Geometry } from "geojson";
import { GeoJSON, LatLngBounds, LatLngExpression } from "leaflet";

import { isRequestAborted } from "src/shared/utils/is-request-aborted";

import { getUserId } from "../../../authentication/store/auth.selector";
import { logoutSuccess } from "../../../authentication/store/auth.slice";
import { API_ENDPOINT } from "../../../config/api";
import { fetchWithAuth } from "../../../shared/api/fetch-with-auth";
import { isArcGisError } from "../../../shared/services/error";
import { fetchAllPages } from "../../../shared/utils/fetch-all-pages";
import { RootState } from "../../../store-types";
import {
  CropMap,
  TArcgisMapCropPropsDto,
} from "../shared/dtos/arcgis-map-crop-props.dto";
import { IFarmDto } from "../shared/dtos/farm.dto";
import { IFarmNameTuple } from "../shared/interfaces/farm-name-tuple";
import { IMapPageState } from "../shared/interfaces/map-page-state";
import { FarmNameTuple } from "../shared/models/farm-name-tuple";

import { MAP_PAGE_MODULE_NAME } from "./constants";

const initialMapPageState: IMapPageState = {
  geoJson: new GeoJSON<TArcgisMapCropPropsDto>(),
  geoJsonAlreadyFetchedForFarmSeason: "INITIAL_STATE:NO_GEOJSON_REQUESTED_YET",
  farmLandData: [],
  farmFeatures: undefined, // NOT_YET_LOADED => undefined
  isLoadingGeoJson: false,
  isLoading: false,
  center: [55, 46],
  zoom: 10,
  bounds: new LatLngBounds([
    [54, 84],
    [53, 83],
  ]),
  selectedCropNames: [],
  isDrawerOpen: true,

  // TODO: remove unused
  farmLandCropsGeoFeatures: [],
  farmNameTuples: [],
  selectedFarmName: "",
  defaultFarmName: "",
  cropTypes: [],
};

export const mapPageSlice = createSlice({
  name: MAP_PAGE_MODULE_NAME,
  initialState: initialMapPageState,
  reducers: {
    setIsLoadingGeoJsonAction(state, action: PayloadAction<boolean>): void {
      state.isLoadingGeoJson = action.payload;
    },
    setCenterAction(state, action: PayloadAction<LatLngExpression>): void {
      state.center = action.payload;
    },
    setZoomAction(state, action: PayloadAction<number>): void {
      state.zoom = action.payload;
    },
    setBoundsAction(state, action: PayloadAction<LatLngBounds>): void {
      const bounds = action.payload;

      if (!bounds.isValid()) {
        return;
      }

      state.bounds = bounds;
    },
    setGeoJsonAction(
      state,
      action: PayloadAction<GeoJSON<TArcgisMapCropPropsDto>>
    ): void {
      state.geoJson = action.payload;
    },
    setGeoJsonAlreadyFetchedForFarmSeasonAction(
      state,
      action: PayloadAction<string>
    ): void {
      state.geoJsonAlreadyFetchedForFarmSeason = action.payload;
    },
    setFarmLandCropsGeoFeaturesAction(state, action): void {
      state.farmLandCropsGeoFeatures = action.payload;
    },
    setSelectedCropNamesAction(state, action): void {
      state.selectedCropNames = action.payload;
    },
    setSelectedFarmNameAction(state, action): void {
      state.selectedFarmName = action.payload;
    },
    setFarmNameTuplesAction(state, action): void {
      state.farmNameTuples = action.payload;
    },
    setDefaultFarmNameAction(state, action): void {
      state.defaultFarmName = action.payload;
    },
    setCropTypesAction(state, action): void {
      state.cropTypes = action.payload;
    },
    setFarmLandDataAction(state, action: PayloadAction<TArcgisMapCropPropsDto[]>): void {
      state.farmLandData = action.payload;
    },
    setFarmFeaturesAction(
      state,
      action: PayloadAction<Feature<Geometry, TArcgisMapCropPropsDto>[] | undefined>
    ): void {
      state.farmFeatures = action.payload;
    },
    toggleDrawerOpenAction(state, _: PayloadAction): void {
      state.isDrawerOpen = !state.isDrawerOpen;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(logoutSuccess, (state) => {
        state.farmNameTuples = [];
      })
      .addCase(fetchGeoJsonAction.fulfilled, (state) => {
        state.isLoadingGeoJson = false;
      })
      .addCase(fetchGeoJsonAction.pending, (state) => {
        state.isLoadingGeoJson = true;
      })
      .addCase(fetchGeoJsonAction.rejected, (state, action) => {
        if (isRequestAborted(action.error)) {
          return;
        }
        state.isLoadingGeoJson = false;
      });
  },
});

export const mapReducer = mapPageSlice.reducer;
export const {
  setIsLoadingGeoJsonAction,
  setFarmLandCropsGeoFeaturesAction,
  setSelectedCropNamesAction,
  setSelectedFarmNameAction,
  setFarmNameTuplesAction,
  setDefaultFarmNameAction,
  setCropTypesAction,
  setGeoJsonAction,
  setGeoJsonAlreadyFetchedForFarmSeasonAction,
  setZoomAction,
  setCenterAction,
  setBoundsAction,
  setFarmFeaturesAction,
  setFarmLandDataAction,
  toggleDrawerOpenAction,
} = mapPageSlice.actions;

export const fetchGeoJsonAction = createAsyncThunk<
  void,
  { farmId: string; seasonId: string },
  { state: RootState; signal: AbortSignal }
>(
  `${MAP_PAGE_MODULE_NAME}/fetchGeoJsonAction`,
  async ({ farmId, seasonId }, { dispatch, getState, signal }) => {
    const rootState = getState();
    const mapState: IMapPageState = rootState[MAP_PAGE_MODULE_NAME];

    // TODO: need to prevent multiple calls (find better solution if it's possible)
    const stringComparedByValue = `farmId:[${farmId}]:seasonId[${seasonId}]`;
    if (mapState.geoJsonAlreadyFetchedForFarmSeason === stringComparedByValue) {
      // this is too late here;
      // extraReducers()....addCase(fetchGeoJsonAction.pending
      //   had already changed
      //     state.isLoadingGeoJson = true;
      return; // resolves infinite backend request to /api/FarmLands/getFarmLandGeometriesBySeasonAndFarm
    }
    dispatch(setGeoJsonAlreadyFetchedForFarmSeasonAction("LOADING_NOW"));

    const geoParams = new URLSearchParams({
      farmId,
      seasonId,
      withGeometryForSwaggerFalse: "true",
    });
    const geoResponse = await fetchWithAuth(
      `${API_ENDPOINT}/FarmLands/GetFarmLandGeometries?${geoParams}`,
      { method: "GET", signal }
    );
    if (!geoResponse.ok) {
      throw await geoResponse.json();
    }
    const featureCollection: CropMap = await geoResponse.json();

    if (isArcGisError(featureCollection)) {
      const rootCause = "WAIT_UNTIL_FarmLandCropsGeoService_ABSORBS_ARCGIS_TOKEN";
      dispatch(setGeoJsonAlreadyFetchedForFarmSeasonAction(rootCause));
      return;
    }

    const data: TArcgisMapCropPropsDto[] = firstEncountered(featureCollection.features);

    const geoJson = new GeoJSON(featureCollection);
    dispatch(setGeoJsonAction(geoJson));
    dispatch(setGeoJsonAlreadyFetchedForFarmSeasonAction(stringComparedByValue));

    const bounds: LatLngBounds = geoJson.getBounds();
    dispatch(setBoundsAction(bounds));

    try {
      dispatch(setCenterAction(bounds.getCenter()));
    } catch (error) {
      console.warn(error, `EMPTY_GEOMETRIES: cant find center of the map`);
    }

    dispatch(setFarmLandDataAction(data));

    const features: Feature<Geometry, TArcgisMapCropPropsDto>[] =
      featureCollection.features;
    dispatch(setFarmFeaturesAction(features));
  }
);

/**
 * Грузит хозяйства текущего пользователя. Возвращает их число, а `null` — если
 * список не запрашивался (нет `userId` или за время загрузки сменился пользователь).
 */
export const fetchAvailableFarmsAction = createAsyncThunk<
  number | null,
  void,
  { state: RootState }
>(
  `${MAP_PAGE_MODULE_NAME}/fetchAvailableFarmsAction`,
  async (_, { dispatch, getState }) => {
    const userId = getUserId(getState());
    // `/ims/me` без `userId` — BFF не связал сессию IMS с записью AppUser.
    // Запрос на `AppUsersV2/null/farms` бессмыслен, хозяйств у такой сессии нет.
    if (!userId) {
      console.warn("fetchAvailableFarmsAction: /ims/me returned no userId");
      return null;
    }

    const farmDtos = await fetchAllPages<IFarmDto>(async (pageNumber, pageSize) => {
      const searchParams = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
      });
      const farmsResponse = await fetchWithAuth(
        `${API_ENDPOINT}/AppUsersV2/${userId}/farms?${searchParams}`
      );
      if (!farmsResponse.ok) {
        throw await farmsResponse.json();
      }
      return farmsResponse.json();
    });
    const farmNameTuples = farmDtos
      .map(
        (farmDto: IFarmDto) =>
          new FarmNameTuple({
            id: farmDto.id,
            nameArcgis: farmDto.name,
            nameEkocrop: farmDto.name,
          })
      )
      .sort((prev: IFarmNameTuple, next: IFarmNameTuple) =>
        prev.nameEkocrop > next.nameEkocrop
          ? 1
          : prev.nameEkocrop < next.nameEkocrop
            ? -1
            : 0
      );

    if (getUserId(getState()) !== userId) {
      return null;
    }

    dispatch(setFarmNameTuplesAction(farmNameTuples));
    return farmNameTuples.length;
  }
);

function firstEncountered(
  features: Feature<Geometry, TArcgisMapCropPropsDto>[]
): TArcgisMapCropPropsDto[] {
  const mapCropByArcgisId = new Map<string | number, TArcgisMapCropPropsDto>();
  for (const x of features) {
    const featureId = x.id || x.properties.GDB_ARCHIVE_OID;
    if (!featureId) {
      continue;
    }
    if (mapCropByArcgisId.has(featureId)) {
      continue;
    }
    mapCropByArcgisId.set(featureId, x.properties);
  }

  return Array.from(mapCropByArcgisId.values());
}
